import { all, call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action, AnyAction } from "typescript-fsa";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { NotificationActions } from "@com.mgmtp.a12.client/client-core/lib/core/notification";
import { Commands, Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormActivity, FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import {
	DocumentModel,
	DocumentRtServiceFactory,
	Document,
	IGeneratedCodeAccessor
} from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { createCustomNotificationAction } from "../../../../app/actions";
import { GenericObject, getDataOfObjectAtPath, setDataOfObjectAtPath } from "../../../../helpers/objectHelper";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../../localization";
import { saveActivity, validateActivity } from "../../../../utilities/activityUtils";
import { putErrorNotification } from "../../../../utilities/notificationUtils";
import { getValueFromModelPath } from "../../../../utilities/pathHelpers";
import { isString } from "../../../../utilities/typeGuardUtils";
import { PATH_TO_STATUS } from "../../../../utilities/variables/documentModelPaths";
import { createEnableValidationStateFormEngineAction, VALIDATION_ACTIONS } from "../../../../utilities/workflowUtils";
import { TASK_DETAIL_ACTIVITY_ID } from "../../../processOverview/store/actions";
import { assertNonNullable } from "../../../../utilities/assert";

import { acceptEditAntragAction, requestEditableAction } from "../actions";
import { ProzessschrittStatus } from "../constants";

function isAcceptEditButtonAction(action: AnyAction): boolean {
	return (
		FormEngineActions.event.match(action) &&
		Events.eventButton.match(action.payload.engineEvent) &&
		["accept_edit"].includes(action.payload.engineEvent.payload.name)
	);
}

type DocumentModelWithCodeAccessor = DocumentModel & {
	generatedCodeAccessor: IGeneratedCodeAccessor;
};

type DataWithDocument = GenericObject & {
	modelId: string;
	document: Document;
};

export function* acceptEditSaga(): SagaGenerator<void> {
	yield* takeLatest((action: AnyAction) => {
		return isAcceptEditButtonAction(action) || acceptEditAntragAction.match(action);
	}, handle);
}

type EventAction = Action<FormEngineActions.FormEngineEventActions>;

function* handle(action: EventAction): SagaGenerator<void> {
	const { activityId } = action.payload;

	const antragData = yield* select(ActivitySelectors.data(activityId));
	const processData = yield* select(ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID));
	if (
		!antragData ||
		!processData ||
		!FormActivity.Data.SingleDocumentData.isInstance(antragData) ||
		!FormActivity.Data.SingleDocumentData.isInstance(processData)
	) {
		return;
	}

	const documentModel = yield* select(
		ModelSelectors.modelInScene({ activityId, modelType: "document" }, Model.isDocumentModel)
	);

	if (!documentModel) {
		return;
	}

	const modelRoot = documentModel.content.modelRoot;

	const antragPath = getPathToAntrag(modelRoot);

	if (antragPath.length === 0) {
		yield* call(
			putErrorNotification,
			RESOURCE_KEYS.features.notification.error.unexpectedError,
			'No "Antrag" marker set in the document model. The group that contains the data has to be annotated with key -> mf_runtime_antrag_marker and value -> antrag!',
			activityId,
			"acceptEdit"
		);
		return;
	}

	const newAntragData = getDataOfObjectAtPath(antragPath, antragData);
	if (!newAntragData) {
		return;
	}

	const valid = yield* call(validateAntragActivity, activityId, antragData);
	if (!valid) {
		yield* put(
			createCustomNotificationAction({
				title: localizableFromLocaleResources(RESOURCE_KEYS.wf.notification.error.validationFailedTitle),
				message: localizableFromLocaleResources(RESOURCE_KEYS.wf.notification.error.validationFailedMessage),
				severity: "error",
				activityId,
				group: "acceptEdit"
			})
		);
		return;
	}

	const isSaved = yield* call(saveActivity, activityId);
	if (!isSaved) {
		yield* put(
			createCustomNotificationAction({
				title: localizableFromLocaleResources(RESOURCE_KEYS.wf.notification.error.savingFailedTitle),
				message: localizableFromLocaleResources(RESOURCE_KEYS.wf.notification.error.savingFailedMessage),
				severity: "error",
				activityId,
				group: "acceptEdit"
			})
		);
		return;
	}

	const processDataWithNewAntrag = setDataOfObjectAtPath(antragPath, processData, newAntragData);
	const modelId = processData.document.id.split("/")[0];
	const processDataWithModelId = { modelId, ...processDataWithNewAntrag };
	const validationProvider = (documentModel as DocumentModelWithCodeAccessor).generatedCodeAccessor;
	assertNonNullable(modelId);
	assertNonNullable(validationProvider);
	const processDocument = (processDataWithModelId as DataWithDocument).document;
	assertNonNullable(processDocument);
	const rtService = DocumentRtServiceFactory.createDocumentRtService(validationProvider);
	const { modelId: ignore, ...processDocumentWithoutModelId } = processDocument;
	const computationResult = rtService.compute(processDocumentWithoutModelId);
	const computed = computationResult.appliedTo(processDocument);
	const newData = { document: { ...computed } };

	yield* all([
		put(NotificationActions.removeByActivityIds([activityId])),
		put(
			ActivityActions.setData({
				activityId: TASK_DETAIL_ACTIVITY_ID,
				data: newData
			})
		),
		put(
			FormEngineActions.command({
				activityId,
				engineEvent: Commands.setReadonly(true)
			})
		)
	]);

	yield* put(requestEditableAction({}));
}

function* validateAntragActivity(
	activityId: string,
	antragData: FormActivity.Data.SingleDocumentData
): SagaGenerator<boolean> {
	const prevProzessschritteStatus = getValueFromModelPath(PATH_TO_STATUS, antragData.document);
	if (!isString(prevProzessschritteStatus)) {
		return false;
	}

	// set the variables for the validations to only validate the antrags data
	yield* put(createProzessschritteStatusChangeAction(activityId, ProzessschrittStatus.Neu));
	yield* put(createEnableValidationStateFormEngineAction(activityId, VALIDATION_ACTIONS.COMPLETE));
	// validate the computations before setting the new document data
	const valid = yield* call(validateActivity, activityId);
	// reset the variables for the validation
	yield* put(createEnableValidationStateFormEngineAction(activityId, VALIDATION_ACTIONS.NULL));
	yield* put(createProzessschritteStatusChangeAction(activityId, prevProzessschritteStatus));

	return valid;
}

function getPathToAntrag(element: DocumentModel.Element) {
	return findPathToGroupWithAntragMarkerAnnotation(element).reverse();
}

function findPathToGroupWithAntragMarkerAnnotation(element: DocumentModel.Element) {
	if (
		element.annotations &&
		element.annotations.find(e => e.name === "mf_runtime_antrag_marker")?.value === "antrag"
	) {
		return [element.name];
	}

	if (element.type === "Group") {
		let result: string[] = [];
		for (const child of element.elements) {
			result = findPathToGroupWithAntragMarkerAnnotation(child);

			if (result.length >= 1) {
				break;
			}
		}

		if (result.length >= 1) {
			result.push(element.id === "RootGroup" ? "document" : element.name);
		}

		return result;
	}

	return [];
}

function createProzessschritteStatusChangeAction(activityId: string, value: string) {
	return FormEngineActions.event({
		activityId,
		engineEvent: Events.valueChange({
			path: [
				{ elementName: "root", index: 1 },
				{ elementName: "genehmigungsverfahren", index: 1 },
				{ elementName: "prozessschritte", index: 1 },
				{ elementName: "Status", index: 1 }
			],
			value
		})
	});
}
